import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LANGUAGES = ['en', 'it', 'de', 'es', 'fr', 'pt', 'ja', 'ko', 'zh', 'ru'];
export const DEFAULT_LANGUAGE = 'en';

export const LANGUAGE_NAMES = {
  en: 'English',
  it: 'Italiano',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
  ja: '日本語',
  ko: '한국어',
  zh: '中文',
  ru: 'Русский',
};

// Each locale is ~200KB of JSON. Bundling all 10 into the main entry meant
// every visitor downloaded and parsed ~2MB of JSON they'd never read, on
// every page, in every language - the main driver of the site's mobile LCP.
// This backend loads only the active language as its own chunk, on demand.
const localeLoaders = {
  en: () => import('./locales/en.json'),
  it: () => import('./locales/it.json'),
  de: () => import('./locales/de.json'),
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
  pt: () => import('./locales/pt.json'),
  ja: () => import('./locales/ja.json'),
  ko: () => import('./locales/ko.json'),
  zh: () => import('./locales/zh.json'),
  ru: () => import('./locales/ru.json'),
};

const lazyLocaleBackend = {
  type: 'backend',
  read(language, namespace, callback) {
    const load = localeLoaders[language] || localeLoaders[DEFAULT_LANGUAGE];
    load()
      .then((mod) => callback(null, mod.default))
      .catch((err) => callback(err, null));
  },
};

i18n.use(lazyLocaleBackend).use(initReactI18next);

// Initializes i18next with the given language and resolves once that
// language's resource chunk has loaded. Call once, before the first render,
// so hydration matches the prerendered markup instead of flashing raw keys.
export const initI18n = (lng) =>
  i18n.init({
    lng,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: ['translation'],
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

// Get language from URL path
export const getLanguageFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
    return segments[0];
  }
  return DEFAULT_LANGUAGE;
};

// Remove language prefix from path
export const removeLanguageFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
    return '/' + segments.slice(1).join('/') || '/';
  }
  return pathname;
};

// Add language prefix to path
export const addLanguageToPath = (pathname, lang) => {
  const cleanPath = removeLanguageFromPath(pathname);
  if (lang === DEFAULT_LANGUAGE) {
    return cleanPath;
  }
  return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
};

export default i18n;
